export interface EncryptedCredential {
  version: 1;
  iterations: number;
  salt: string;
  iv: string;
  ciphertext: string;
}

const ITERATIONS = 210_000;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function bytesToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function base64ToBytes(value: string): Uint8Array {
  const raw = atob(value);
  return Uint8Array.from(raw, char => char.charCodeAt(0));
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function deriveKey(password: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  if (password.length < 8) throw new Error('本地主密码至少需要 8 个字符');
  const material = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: toArrayBuffer(salt), iterations },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptCredential(
  credential: string,
  password: string,
): Promise<EncryptedCredential> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt, ITERATIONS);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    key,
    encoder.encode(credential),
  );
  return {
    version: 1,
    iterations: ITERATIONS,
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
  };
}

export async function decryptCredential(
  payload: EncryptedCredential,
  password: string,
): Promise<string> {
  if (payload.version !== 1) throw new Error('不支持的密钥格式');
  const salt = base64ToBytes(payload.salt);
  const iv = base64ToBytes(payload.iv);
  const key = await deriveKey(password, salt, payload.iterations);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(base64ToBytes(payload.ciphertext)),
  );
  return decoder.decode(plaintext);
}

export class MemoryCredentialVault {
  private readonly credentials = new Map<string, string>();

  set(provider: string, credential: string): void {
    this.credentials.set(provider, credential);
  }

  get(provider: string): string | undefined {
    return this.credentials.get(provider);
  }

  clear(provider?: string): void {
    if (provider) this.credentials.delete(provider);
    else this.credentials.clear();
  }
}

const DB_NAME = 'xiangqi-credential-vault';
const STORE_NAME = 'credentials';

function openCredentialDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('无法打开本地密钥库'));
  });
}

export async function saveEncryptedCredential(
  provider: string,
  payload: EncryptedCredential,
): Promise<void> {
  const db = await openCredentialDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(payload, provider);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('无法保存密钥'));
    });
  } finally {
    db.close();
  }
}

export async function loadEncryptedCredential(
  provider: string,
): Promise<EncryptedCredential | undefined> {
  const db = await openCredentialDb();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE_NAME).objectStore(STORE_NAME).get(provider);
      request.onsuccess = () => resolve(request.result as EncryptedCredential | undefined);
      request.onerror = () => reject(request.error ?? new Error('无法读取密钥'));
    });
  } finally {
    db.close();
  }
}

export async function deleteEncryptedCredential(provider: string): Promise<void> {
  const db = await openCredentialDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(provider);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('无法删除密钥'));
    });
  } finally {
    db.close();
  }
}
